/** Role identifier for the roles section (owner, global admin, team lead, team member, unassigned). */
export const ROLE = {
  OWNER: 1,
  GLOBAL_ADMIN: 2,
  TEAM_LEAD: 3,
  TEAM_MEMBER: 4,
  UNASSIGNED: 5,
} as const;

/** Numeric role id for the roles section (owner, global admin, team lead, team member, unassigned). */
export type RolesSectionRole = (typeof ROLE)[keyof typeof ROLE];

export interface Team {
  id: number;
  name: string;
  organisation_id: number;
  creator_id: number;
  created_at: string;
  updated_at: string;
  members_count: number;
  description: string;
}

export type PeoplePerTeamRole = 'teams_team_lead' | 'teams_team_member';

export type TeamRoleName =
  | 'teams_team_lead'
  | 'teams_team_member'
  | 'teams_global_admin'
  | 'unassigned';

export type UnassignedPeopleStatus = 'active' | 'inactive' | 'onboarding';

import type { Sort } from './sort';

export interface GetPeoplePerTeamFilters {
  current_page?: number;
  per_page?: number;
  search?: string;
  country_ids?: number[];
  roles?: PeoplePerTeamRole[];
  job_ids?: number[];
  employee_types?: string[];
  statuses?: UnassignedPeopleStatus[];
  sort?: Sort;
}

export interface GetUnassignedPeopleFilters {
  current_page?: number;
  per_page?: number;
  search?: string;
  country_ids?: number[];
  roles?: string[];
  job_ids?: number[];
  team_ids?: number[];
  employee_types?: string[];
  statuses?: UnassignedPeopleStatus[];
  sort?: Sort;
}

export interface GetPeopleByRoleFilters {
  current_page?: number;
  per_page?: number;
  search?: string;
  country_ids?: number[];
  roles?: string[];
  job_ids?: number[];
  team_ids?: number[];
  employee_types?: string[];
  statuses?: UnassignedPeopleStatus[];
  sort?: Sort;
}

export interface GetPeopleByRoleResponse {
  people: TeamMember[];
  role_counts: {
    client: number;
    global_admin: number;
    team_lead: number;
    team_member: number;
  };
}

export interface TeamMember {
  user_id: number;
  team_organisation_user_id: number;
  organisation_id?: number;
  first_name: string;
  last_name: string;
  country_id: number;
  country_name: string;
  status: string;
  team_id?: number;
  team_role_id?: number;
  team_role_name?: string;
  role_name?: string;
  /** API may return { role_id, role_name } or { id, name } */
  roles?: { role_id?: number; role_name?: string }[];
  job_title?: string;
  team_name?: string;
  employee_status?: string;
  role_id?: number;
  entity_id?: number | null;
  entity_name?: string | null;
  organisation_user_id?: number;
  employment_details_id?: number;
  contractor_details_id?: number | null;
  email?: string;
}

export interface AddUserToTeamRequest {
  organisation_id: number;
  team_id: number;
  user_id: number;
}

export interface AddUserToTeamResponse {
  team_id: number;
  team_organisation_user_id: number;
  organisation_id: number;
}

export interface BulkAddUsersToTeamRequest {
  organisation_id: number;
  team_id: number;
  user_ids: number[];
}

export interface BulkAddUsersToTeamResponse {
  organisation_id: number;
  team_id: number;
  team_organisation_user_ids: number[];
}

export interface UpdateTeamMemberRolesRequest {
  user_id: number;
  team_organisation_user_id: number;
  team_id: number;
  organisation_id: number;
  role: TeamRoleName;
}

export interface BulkUpdateTeamMemberRolesRequest {
  role: TeamRoleName;
  organisation_id: number;
  team_id: number;
  team_organisation_user_ids?: number[];
  user_ids?: number[];
}

export interface RemoveUserFromTeamRequest {
  team_id: number;
  organisation_id: number;
  team_organisation_user_id: number;
  role: PeoplePerTeamRole;
}

export interface TransferTeamMemberRequest {
  organisation_id: number;
  user_id: number;
  team_organisation_user_id: number;
  current_team_id: number;
  new_team_id: number;
  current_role: TeamRoleName;
}

export interface TransferTeamMemberResponse {
  team_id: number;
  team_organisation_user_id: number;
  organisation_id: number;
}

export interface RolesPeopleCount {
  teams_client: number;
  teams_global_admin: number;
  teams_team_admin: number;
  teams_team_lead: number;
  teams_team_member: number;
}
